package co.maytes.standupbot.dashboard.features.analytics

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.widgets.svg.Svg
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgCircle
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgLine
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgPolyline
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgRect
import co.maytes.standupbot.dashboard.components.widgets.svg.SvgText
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.shared.dto.analytics.VelocityDto
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import kotlinx.datetime.Instant
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import kotlin.math.max

private const val WIDTH = 800
private const val HEIGHT = 240
private const val PAD_L = 48
private const val PAD_R = 16
private const val PAD_T = 16
private const val PAD_B = 32

@Composable
fun WeeklyActivityChart(dto: VelocityDto) {
    val weeks = dto.weeks.sortedBy { it.weekStart }
    val isEmpty = weeks.isEmpty() ||
        weeks.all { it.standupsPosted == 0 && it.distinctContributors == 0 && it.sectionsGenerated == 0 }

    val standupsColor = ChartPalette.at(0)
    val contributorsColor = ChartPalette.at(1)
    val sectionsColor = ChartPalette.at(2)

    ChartShell(
        eyebrow = "WEEKLY ACTIVITY",
        title = "Standups, contributors, sections",
        subtitle = "Standups posted, distinct contributors, and total sections generated each week.",
        isEmpty = isEmpty,
        legend = listOf(
            LegendItem("Standups posted", standupsColor),
            LegendItem("Contributors", contributorsColor),
            LegendItem("Sections", sectionsColor),
        ),
        emptyTitle = "No weekly activity yet",
        emptyMessage = "Posts, contributors, and sections plot here as your team runs standups week over week.",
        emptyIcon = { LucideUsers(size = 26) },
    ) {
        var tooltip by remember { mutableStateOf<WeekHover?>(null) }
        val drawn = rememberChartIntroPlayed()

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .styleModifier { property("position", "relative") },
        ) {
            Svg(attrs = {
                attr("viewBox", "0 0 $WIDTH $HEIGHT")
                attr("width", "100%")
                attr("height", "${HEIGHT}px")
                attr("role", "img")
                attr("aria-label", "Weekly standup activity: standups posted with contributor overlay")
            }) {
                val maxY = max(
                    1,
                    weeks.maxOf { max(it.standupsPosted, it.distinctContributors) },
                )
                renderWeeklyGrid(maxY)

                val barAreaWidth = (WIDTH - PAD_L - PAD_R).toDouble()
                val slot = if (weeks.isNotEmpty()) barAreaWidth / weeks.size else 0.0
                val barWidth = (slot * 0.6).toInt().coerceIn(8, 56)

                val centers = weeks.indices.map { i ->
                    PAD_L + (i * slot + slot / 2.0).toInt()
                }

                weeks.forEachIndexed { i, w ->
                    val cx = centers[i]
                    val barX = cx - barWidth / 2
                    val h = barHeight(w.standupsPosted, maxY)
                    val yTop = HEIGHT - PAD_B - h

                    SvgRect(attrs = {
                        attr("x", "$barX")
                        attr("y", "$yTop")
                        attr("width", "$barWidth")
                        attr("height", "$h")
                        attr("rx", "3")
                        attr("fill", standupsColor)
                        style {
                            property("transform-origin", "${cx}px ${HEIGHT - PAD_B}px")
                            property("transform", if (drawn) "scaleY(1)" else "scaleY(0)")
                            property(
                                "transition",
                                "${ChartMotion.intro("transform", i)}, ${ChartMotion.update("y", "height")}",
                            )
                            property("pointer-events", "none")
                        }
                    })
                }

                val linePoints = weeks.mapIndexed { i, w ->
                    centers[i] to yFor(w.distinctContributors, maxY)
                }
                if (linePoints.size >= 2) {
                    SvgPolyline(attrs = {
                        attr("points", linePoints.toPointsAttr())
                        attr("fill", "none")
                        attr("stroke", contributorsColor)
                        attr("stroke-width", "2")
                        attr("stroke-linecap", "round")
                        attr("stroke-linejoin", "round")
                        val length = estimatedLength(linePoints)
                        style {
                            property("stroke-dasharray", "$length")
                            property("stroke-dashoffset", if (drawn) "0" else "$length")
                            property("transition", ChartMotion.intro("stroke-dashoffset"))
                        }
                    })
                }
                linePoints.forEachIndexed { i, (x, y) ->
                    SvgCircle(attrs = {
                        attr("cx", "$x")
                        attr("cy", "$y")
                        attr("r", "3.5")
                        attr("fill", contributorsColor)
                        attr("stroke", Palette.surface.cssRgb())
                        attr("stroke-width", "2")
                        style {
                            property("opacity", if (drawn) "1" else "0")
                            property("transition", ChartMotion.intro("opacity", i))
                        }
                    })
                }

                weeks.forEachIndexed { i, w ->
                    val cx = centers[i]
                    val yTop = HEIGHT - PAD_B - barHeight(w.standupsPosted, maxY)
                    SvgRect(attrs = {
                        attr("x", "${PAD_L + (i * slot).toInt()}")
                        attr("y", "$PAD_T")
                        attr("width", "${slot.toInt()}")
                        attr("height", "${HEIGHT - PAD_T - PAD_B}")
                        attr("fill", "transparent")
                        style { property("cursor", "pointer") }
                        onMouseEnter {
                            tooltip = WeekHover(
                                x = cx,
                                y = yTop,
                                content = TooltipContent(
                                    title = formatWeekLabel(w.weekStart),
                                    subtitle = "Week of ${formatWeekLabel(w.weekStart)}",
                                    rows = listOf(
                                        TooltipContent.Row(
                                            label = "Standups posted",
                                            value = w.standupsPosted.toString(),
                                            color = standupsColor,
                                        ),
                                        TooltipContent.Row(
                                            label = "Contributors",
                                            value = w.distinctContributors.toString(),
                                            color = contributorsColor,
                                        ),
                                        TooltipContent.Row(
                                            label = "Sections",
                                            value = w.sectionsGenerated.toString(),
                                            color = sectionsColor,
                                        ),
                                    ),
                                ),
                            )
                        }
                        onMouseLeave { tooltip = tooltip?.copy(content = null) }
                    })
                }

                weeks.forEachIndexed { i, w ->
                    SvgText(
                        text = formatWeekLabel(w.weekStart),
                        attrs = {
                            attr("x", "${centers[i]}")
                            attr("y", "${HEIGHT - PAD_B / 3}")
                            attr("text-anchor", "middle")
                            attr("font-size", "11")
                            attr("font-weight", "500")
                            attr("fill", Palette.textTertiary.cssRgb())
                            attr("font-family", "Inter, sans-serif")
                        },
                    )
                }
            }

            val hovered = tooltip
            if (hovered != null) {
                ChartTooltip(
                    content = hovered.content,
                    x = hovered.x,
                    y = hovered.y,
                    visible = hovered.content != null,
                )
            }
        }
    }
}

private data class WeekHover(val x: Int, val y: Int, val content: TooltipContent?)

private fun barHeight(value: Int, maxY: Int): Int {
    val area = (HEIGHT - PAD_T - PAD_B).toDouble()
    val ratio = value.toDouble() / maxY
    return (ratio * area).toInt().coerceAtLeast(0)
}

private fun yFor(value: Int, maxY: Int): Int {
    val area = (HEIGHT - PAD_T - PAD_B).toDouble()
    val ratio = value.toDouble() / maxY
    return (HEIGHT - PAD_B - ratio * area).toInt()
}

private fun List<Pair<Int, Int>>.toPointsAttr(): String =
    joinToString(" ") { (x, y) -> "$x,$y" }

private fun estimatedLength(points: List<Pair<Int, Int>>): Int {
    var len = 0.0
    for (i in 1 until points.size) {
        val dx = (points[i].first - points[i - 1].first).toDouble()
        val dy = (points[i].second - points[i - 1].second).toDouble()
        len += kotlin.math.sqrt(dx * dx + dy * dy)
    }
    return len.toInt().coerceAtLeast(1)
}

@Composable
private fun renderWeeklyGrid(maxY: Int) {
    val yBottom = HEIGHT - PAD_B
    val yTop = PAD_T
    val steps = 4
    SvgRect(attrs = {
        attr("x", "$PAD_L")
        attr("y", "$yTop")
        attr("width", "${WIDTH - PAD_L - PAD_R}")
        attr("height", "${yBottom - yTop}")
        attr("fill", Palette.surfaceSubtle.cssRgb())
        attr("opacity", "0.5")
        attr("rx", "6")
    })
    for (i in 0..steps) {
        val y = yBottom - (i * (yBottom - yTop) / steps)
        SvgLine(attrs = {
            attr("x1", "$PAD_L")
            attr("x2", "${WIDTH - PAD_R}")
            attr("y1", "$y")
            attr("y2", "$y")
            attr("stroke", Palette.border.cssRgb())
            attr("stroke-width", "1")
            attr("opacity", if (i == 0) "0.9" else "0.45")
            attr("stroke-dasharray", if (i == 0) "0" else "1 5")
            attr("stroke-linecap", "round")
        })
        val value = maxY * i / steps
        SvgText(
            text = "$value",
            attrs = {
                attr("x", "${PAD_L - 12}")
                attr("y", "${y + 4}")
                attr("text-anchor", "end")
                attr("font-size", "11")
                attr("font-weight", "500")
                attr("fill", Palette.textTertiary.cssRgb())
                attr("font-family", "Inter, sans-serif")
                attr("style", "font-variant-numeric: tabular-nums")
            },
        )
    }
}

internal fun formatWeekLabel(instant: Instant): String {
    val ldt = instant.toLocalDateTime(TimeZone.UTC)
    val month = when (ldt.monthNumber) {
        1 -> "Jan"; 2 -> "Feb"; 3 -> "Mar"; 4 -> "Apr"
        5 -> "May"; 6 -> "Jun"; 7 -> "Jul"; 8 -> "Aug"
        9 -> "Sep"; 10 -> "Oct"; 11 -> "Nov"; 12 -> "Dec"
        else -> ldt.month.name.take(3)
    }
    return "$month ${ldt.dayOfMonth}"
}
